import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';

const DAYS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const APPLIANCE_ICONS = { robot: '🤖', four: '🫙', plaque: '🔥', 'robot+four': '🤖🫙' };
const APPLIANCE_LABELS = { robot: 'Robot Silvercrest', four: 'Four', plaque: 'Plaque', 'robot+four': 'Robot + Four' };
// Prix moyens supermarché français (€) par mot-clé dans le nom de l'article
const PRICE_DB = {
  // Viandes
  'poulet entier': 7.5, 'poulet':3.5, 'cuisses de poulet':4.5, 'blanc de poulet':5, 'dinde':5,
  'bœuf':6, 'viande hachée':5, 'steak':7, 'lardons':2, 'jambon':2.5, 'saucisse':3,
  'sardines':2, 'thon':2, 'saumon':6, 'cabillaud':6, 'merlu':5, 'lieu':4, 'poisson':5,
  // Légumes
  'carottes':1.2, 'courgettes':1.5, 'aubergines':1.8, 'poireaux':1.5, 'pommes de terre':1.5,
  'tomates':2, 'tomates cerises':2.5, 'tomates pelées':1, 'oignons':1, 'ail':0.8,
  'poivrons':2, 'épinards':2, 'brocolis':1.8, 'champignons':2, 'haricots verts':1.5,
  'céleri':1.2, 'navets':1, 'panais':1.5, 'salade':1.2, 'concombre':1,
  // Fruits
  'citron':0.8, 'citron confit':1.5, 'pommes':2, 'olives':2,
  // Féculents & épicerie
  'pâtes':1.2, 'riz':1.5, 'semoule':1.2, 'farine':0.8, 'pain':1.5, 'pain de mie':1.5,
  'pain pita':1.5, 'galette':1.5, 'pâte brisée':1.5, 'pâte feuilletée':2, 'pâte à pizza':1.5,
  'lasagnes':2, 'gnocchi':2, 'macaronis':1.2, 'spaghetti':1.2,
  'lentilles':1.5, 'pois chiches':1.2, 'haricots blancs':1.2,
  'farine':0.8, 'chapelure':1,
  // Produits laitiers
  'lait':1.5, 'beurre':2, 'crème':1.5, 'crème fraîche':1.5, 'yaourt':2, 'fromage frais':2,
  'emmental':2.5, 'gruyère':2.5, 'chèvre':2.5, 'parmesan':3, 'mozzarella':2, 'comté':3,
  'œufs':2.5, 'oeuf':2.5,
  // Conserves & condiments
  'huile':3, 'sauce soja':2, 'moutarde':1.5, 'vinaigre':1, 'bouillon':1.5,
  'tomates pelées':1, 'maïs':1, 'lait de coco':1.5, 'curry':2, 'cumin':1.5,
  'herbes':1, 'basilic':1.5, 'thym':1, 'persil':1, 'coriandre':1.5, 'aneth':1.5,
  'muscade':1.5, 'cannelle':1.5,
  // Boissons
  'eau':2, 'jus':2, 'café':4, 'thé':3,
  // Autres
  'houmous':2, 'pesto':2.5, 'confiture':2.5, 'chocolate':2,
};

function estimateItemPrice(name, quantity) {
  const n = name.toLowerCase();
  // Chercher correspondance dans PRICE_DB (du plus long au plus court pour prioriser précision)
  const keys = Object.keys(PRICE_DB).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (n.includes(key)) {
      let price = PRICE_DB[key];
      // Multiplier si quantité numérique détectée
      if (quantity) {
        const qMatch = quantity.match(/(\d+(?:[.,]\d+)?)\s*(?:kg|kilo)/i);
        const litreMatch = quantity.match(/(\d+(?:[.,]\d+)?)\s*(?:l|litre)/i);
        const unitMatch = quantity.match(/^(\d+)$/);
        if (qMatch) price = price * parseFloat(qMatch[1].replace(',', '.'));
        else if (litreMatch) price = price * parseFloat(litreMatch[1].replace(',', '.'));
        else if (unitMatch && parseInt(unitMatch[1]) > 1) price = price * Math.min(parseInt(unitMatch[1]), 4);
      }
      return price;
    }
  }
  return 2.0; // prix par défaut si inconnu
}

function estimateTotal(items) {
  return items.filter(i => !i.done).reduce((sum, i) => sum + estimateItemPrice(i.name, i.quantity), 0);
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function toISO(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }

export default function Meals() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightDate = searchParams.get('date');
  const highlightRef = useRef(null);
  const [editingIngredients, setEditingIngredients] = useState(false);

  const [weekStart, setWeekStart] = useState(() => {
    const d = searchParams.get('date');
    return d ? getMonday(new Date(d + 'T00:00:00')) : getMonday(new Date());
  });
  const [meals, setMeals] = useState([]);
  const [dislikes, setDislikes] = useState([]);
  const [tab, setTab] = useState('repas');
  const [showForm, setShowForm] = useState(false);
  const [editMeal, setEditMeal] = useState(null);
  const [showDislikes, setShowDislikes] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genResult, setGenResult] = useState('');
  const [aiSuggest, setAiSuggest] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [swapMode, setSwapMode] = useState(false);

  const [mealForm, setMealForm] = useState({ meal_date: toISO(new Date()), meal_type: 'soir', title: '', description: '', ingredients: '', appliance: '', recipe_url: '' });
  const [aiForm, setAiForm] = useState({ ingredients: '', preferences: '' });
  const [dislikeForm, setDislikeForm] = useState({ name: '', type: 'plat' });

  const weekDays = Array.from({ length: 14 }, (_, i) => addDays(weekStart, i));
  const currentMonday = getMonday(new Date());
  const isCurrentWeek = toISO(weekStart) === toISO(currentMonday);

  const load = useCallback(() => {
    const start = toISO(weekStart);
    const end = toISO(addDays(weekStart, 13));
    api.get(`/meals?start=${start}&end=${end}`).then(r => setMeals(r.data)).catch(console.error);
    api.get('/meals/dislikes').then(r => setDislikes(r.data)).catch(console.error);
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener('family_refresh', load); return () => window.removeEventListener('family_refresh', load); }, [load]);

  // Scroll vers la date ciblée depuis les courses
  useEffect(() => {
    if (highlightDate && highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [highlightDate, meals]);

  const today = toISO(new Date());

  const getMeal = (date, type) => meals.find(m => m.meal_date === date && m.meal_type === type);

  const openSlot = (date, type) => {
    const ex = getMeal(date, type);
    setMealForm(ex
      ? { meal_date: ex.meal_date, meal_type: ex.meal_type, title: ex.title, description: ex.description || '', ingredients: ex.ingredients || '', appliance: ex.appliance || '', recipe_url: ex.recipe_url || '' }
      : { meal_date: date, meal_type: type, title: '', description: '', ingredients: '', appliance: '', recipe_url: '' }
    );
    setEditMeal(ex || null);
    setAiSuggest('');
    setAiForm({ ingredients: '', preferences: '' });
    setEditingIngredients(false);
    setSwapMode(false);
    setShowForm(true);
  };

  const saveMeal = async () => {
    if (!mealForm.title.trim()) return;
    setSaving(true);
    try {
      if (editMeal) await api.put(`/meals/${editMeal.id}`, mealForm);
      else await api.post('/meals', mealForm);
      setShowForm(false);
      load();
      setGenResult('🛒 Courses mises à jour');
      setTimeout(() => setGenResult(''), 3000);
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const deleteMeal = async () => {
    if (!editMeal || !window.confirm('Supprimer ce repas ?')) return;
    await api.delete(`/meals/${editMeal.id}`);
    setShowForm(false);
    load();
    setGenResult('🛒 Courses mises à jour');
    setTimeout(() => setGenResult(''), 3000);
  };

  const dislikeMeal = async (meal) => {
    try {
      await api.post('/meals/dislikes', { name: meal.title, type: 'plat' });
      await api.delete(`/meals/${meal.id}`);
      load();
      setGenResult(`👎 "${meal.title}" exclu · 🛒 Courses mises à jour`);
      setTimeout(() => setGenResult(''), 3000);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const replaceMeal = async (meal) => {
    try {
      await api.delete(`/meals/${meal.id}`);
      const week_start = toISO(getMonday(new Date(meal.meal_date + 'T00:00:00')));
      await api.post('/ai/generate-week', { week_start });
      load();
      setGenResult(`🔄 Nouveau repas suggéré · 🛒 Courses mises à jour`);
      setTimeout(() => setGenResult(''), 3000);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const markSkip = async (dateStr, type) => {
    const existing = getMeal(dateStr, type);
    try {
      if (existing) {
        await api.put(`/meals/${existing.id}`, { ...existing, is_skip: 1, title: 'Repas dehors' });
      } else {
        await api.post('/meals', { meal_date: dateStr, meal_type: type, title: 'Repas dehors', is_skip: 1 });
      }
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const unmarkSkip = async (meal) => {
    try {
      await api.delete(`/meals/${meal.id}`);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const swapWith = async (targetMeal) => {
    if (!editMeal || !targetMeal || targetMeal.id === editMeal.id) return;
    try {
      await api.post('/meals/swap', { id1: editMeal.id, id2: targetMeal.id });
      setSwapMode(false);
      setShowForm(false);
      load();
      setGenResult('↔️ Repas échangés · 🛒 Courses mises à jour');
      setTimeout(() => setGenResult(''), 3000);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const generateWeek = async () => {
    setGenerating(true);
    setGenResult('');
    try {
      const week_start_1 = toISO(weekStart);
      const week_start_2 = toISO(addDays(weekStart, 7));
      const [r1, r2] = await Promise.all([
        api.post('/ai/generate-week', { week_start: week_start_1 }),
        api.post('/ai/generate-week', { week_start: week_start_2 })
      ]);
      const total = (r1.data.created || 0) + (r2.data.created || 0);
      const shopping = (r1.data.shoppingAdded || 0) + (r2.data.shoppingAdded || 0);
      const msg = total > 0
        ? `✨ ${total} repas générés${shopping > 0 ? ` · 🛒 ${shopping} articles ajoutés` : ''}`
        : '✅ Menus déjà complets pour les 2 semaines';
      setGenResult(msg);
      load();
      setTimeout(() => setGenResult(''), 5000);
    } catch (e) {
      setGenResult('❌ ' + (e.response?.data?.error || e.message));
      setTimeout(() => setGenResult(''), 5000);
    }
    setGenerating(false);
  };



  const addDislike = async () => {
    if (!dislikeForm.name.trim()) return;
    await api.post('/meals/dislikes', dislikeForm);
    setDislikeForm({ name: '', type: 'plat' });
    load();
  };

  const removeDislike = async (id) => { await api.delete(`/meals/dislikes/${id}`); load(); };

  const getSuggestion = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post('/ai/suggest-meal', { ingredients: aiForm.ingredients, preferences: aiForm.preferences });
      setAiSuggest(data.suggestion);
      const lines = data.suggestion.split('\n');
      if (lines[0]) {
        const clean = lines[0].replace(/^\*+/, '').replace(/\*+$/, '').replace(/^[#\-\s]+/, '').trim();
        setMealForm(f => ({ ...f, title: clean }));
      }
    } catch (e) { setAiSuggest('Erreur : ' + (e.response?.data?.error || e.message)); }
    setAiLoading(false);
  };


  const weekLabel = (() => {
    const end = addDays(weekStart, 13);
    return `${weekStart.getDate()} ${MONTHS_FR[weekStart.getMonth()]} – ${end.getDate()} ${MONTHS_FR[end.getMonth()]}`;
  })();

  return (
    <div>
      <div className="topbar">
        <div className="page-title">🍽️ Repas</div>
      </div>

      <div className="page" style={{ paddingTop: 0 }}>
        <div className="tabs">
          <button className={`tab${tab === 'repas' ? ' active' : ''}`} onClick={() => setTab('repas')}>🍽️ Repas</button>
          <button className={`tab${tab === 'eviter' ? ' active' : ''}`} onClick={() => setTab('eviter')}>👎 Éviter {dislikes.length > 0 && `(${dislikes.length})`}</button>
        </div>

        {/* MESSAGE RÉSULTAT */}
        {genResult && (
          <div style={{ background: genResult.startsWith('❌') ? '#FFEBEE' : '#E8F5E9', color: genResult.startsWith('❌') ? '#C62828' : '#2E7D32', borderRadius: 12, padding: '10px 16px', marginBottom: 12, fontSize: 14, fontWeight: 600 }}>
            {genResult}
          </div>
        )}

        {/* ===== REPAS ===== */}
        {tab === 'repas' && (
          <>
            {/* Navigation + boutons IA */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <button className="btn btn-icon" onClick={() => setWeekStart(addDays(weekStart, -7))}>‹</button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{weekLabel}</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(getMonday(new Date()))} style={{ fontSize: 11 }}>Cette semaine</button>
              </div>
              <button className="btn btn-icon" onClick={() => setWeekStart(addDays(weekStart, 7))}>›</button>
            </div>

            {/* Boutons IA */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn btn-primary" style={{ flex: 1, fontSize: 13 }} onClick={generateWeek} disabled={generating}>
                {generating ? <><span className="spinner" style={{ width: 14, height: 14, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Génération...</> : '✨ Générer les 2 semaines'}
              </button>
            </div>

            {!isCurrentWeek && (
              <div style={{ background: '#FFF8E1', borderRadius: 12, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#E65100', fontWeight: 600 }}>
                📅 Période personnalisée · Les courses ne sont pas gérées automatiquement pour cette période
              </div>
            )}

            {/* En-têtes colonnes */}
            <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr', marginBottom: 4 }}>
              <div />
              <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 800, color: '#8888AA', textTransform: 'uppercase' }}>🌙 Soir</div>
            </div>

            {/* Grille repas */}
            {weekDays.map((day, i) => {
              const dateStr = toISO(day);
              const isToday = dateStr === today;
              const isHighlight = dateStr === highlightDate;
              const soir = getMeal(dateStr, 'soir');
              const isSkip = soir?.is_skip;
              const isWeek2Start = i === 7;
              const week2Label = (() => {
                const d1 = addDays(weekStart, 7);
                const d2 = addDays(weekStart, 13);
                return `${d1.getDate()} ${MONTHS_FR[d1.getMonth()]} – ${d2.getDate()} ${MONTHS_FR[d2.getMonth()]}`;
              })();
              return (
                <React.Fragment key={i}>
                  {isWeek2Start && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, marginBottom: 8, padding: '8px 12px', background: 'linear-gradient(90deg,#EEF0FF 0%,#F5F6FF 100%)', borderRadius: 10, borderLeft: '3px solid #6C63FF' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#6C63FF' }}>📅 Semaine suivante</span>
                      <span style={{ fontSize: 12, color: '#8888AA', fontWeight: 500 }}>{week2Label}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#2E7D32', background: '#E8F5E9', borderRadius: 6, padding: '2px 6px', whiteSpace: 'nowrap' }}>🛒 Courses : jeudi soir</span>
                    </div>
                  )}
                <div ref={isHighlight ? highlightRef : null}
                  className="meal-grid" style={{ gridTemplateColumns: '44px 1fr', borderRadius: 12, transition: 'box-shadow 0.3s', boxShadow: isHighlight ? '0 0 0 3px #6C63FF88' : 'none' }}>
                  <div className={`meal-day-label${isToday ? ' today-label' : ''}`}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{DAYS_FR[i % 7]}</div>
                      <div style={{ fontSize: 16, fontWeight: 800 }}>{day.getDate()}</div>
                    </div>
                  </div>
                  <div style={{ position: 'relative' }}>
                    {isSkip ? (
                      <div style={{ background: '#F5F5F8', borderRadius: 10, padding: '10px 12px', border: '1.5px dashed #CCCCDD', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 13, color: '#AAAACC', fontStyle: 'italic' }}>🍴 Repas dehors</span>
                        <button onClick={() => unmarkSkip(soir)} title="Annuler"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#CCCCDD', lineHeight: 1 }}>×</button>
                      </div>
                    ) : (
                      <>
                        <div className={`meal-slot${soir ? ' filled' : ''}`} onClick={() => !isSkip && openSlot(dateStr, 'soir')}>
                          {soir ? (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className="meal-type-label" style={{ color: '#6C63FF' }}>🌙</div>
                                {soir.appliance && APPLIANCE_ICONS[soir.appliance] && (
                                  <span title={APPLIANCE_LABELS[soir.appliance]} style={{ fontSize: 13, lineHeight: 1 }}>{APPLIANCE_ICONS[soir.appliance]}</span>
                                )}
                              </div>
                              <div className="meal-title-text">{soir.title}</div>
                              {(soir.prep_time || soir.cook_time) && (
                                <div style={{ fontSize: 10, color: '#8888AA', marginTop: 2 }}>
                                  ⏱️ {(soir.prep_time || 0) + (soir.cook_time || 0)} min
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                              <div style={{ fontSize: 11, color: '#CCCCDD' }}>+ Ajouter</div>
                              <button onClick={e => { e.stopPropagation(); markSkip(dateStr, 'soir'); }}
                                title="Marquer comme repas dehors"
                                style={{ background: 'none', border: '1px dashed #CCCCDD', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#AAAACC', padding: '2px 6px', lineHeight: 1.4 }}>
                                🍴 Dehors
                              </button>
                            </div>
                          )}
                        </div>
                        {soir && (
                          <div style={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 1 }}>
                            <button onClick={e => { e.stopPropagation(); replaceMeal(soir); }}
                              title="Autre suggestion (ce plat reste disponible)"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.45, lineHeight: 1, padding: '2px 3px' }}>
                              🔄
                            </button>
                            <button onClick={e => { e.stopPropagation(); dislikeMeal(soir); }}
                              title="Je n'aime pas ce plat (ne plus le proposer)"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.45, lineHeight: 1, padding: '2px 3px' }}>
                              👎
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                </React.Fragment>
              );
            })}

            <div className="text-muted text-center mt-12" style={{ fontSize: 11 }}>
              🔄 Autre suggestion (le plat reste disponible) · 👎 Ne plus proposer ce plat
            </div>
          </>
        )}

        {/* ===== COURSES ===== */}

        {/* ===== PLATS À ÉVITER ===== */}
        {tab === 'eviter' && (
          <>
            <div style={{ background: '#FFF8E1', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13, color: '#E65100' }}>
              <strong>👎 Plats & ingrédients à éviter</strong><br />
              Claude ne reproposera plus ces éléments lors de la génération automatique.
            </div>

            {/* Ajouter manuellement */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Ajouter quelque chose à éviter</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="form-input" placeholder="Ex: brandade, chou-fleur..." value={dislikeForm.name} onChange={e => setDislikeForm({ ...dislikeForm, name: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && addDislike()} style={{ flex: 1 }} />
                <select className="form-input" value={dislikeForm.type} onChange={e => setDislikeForm({ ...dislikeForm, type: e.target.value })} style={{ width: 120 }}>
                  <option value="plat">Plat</option>
                  <option value="ingredient">Ingrédient</option>
                </select>
              </div>
              <button className="btn btn-primary btn-sm" onClick={addDislike} style={{ width: '100%' }}>+ Ajouter</button>
            </div>

            {dislikes.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">👍</div>
                <div className="empty-text">Vous aimez tout !<br />Cliquez sur 👎 sur un repas pour l'exclure.</div>
              </div>
            )}

            {dislikes.filter(d => d.type === 'plat').length > 0 && (
              <>
                <div className="section-title">🍽️ Plats à éviter</div>
                {dislikes.filter(d => d.type === 'plat').map(d => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: 'white', borderRadius: 10, marginBottom: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <span style={{ fontSize: 18 }}>👎</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{d.name}</span>
                    {d.added_by_name && <span className="text-muted" style={{ fontSize: 11 }}>{d.added_by_name}</span>}
                    <button onClick={() => removeDislike(d.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#CCCCDD' }} title="Retirer de la liste">×</button>
                  </div>
                ))}
              </>
            )}

            {dislikes.filter(d => d.type === 'ingredient').length > 0 && (
              <>
                <div className="section-title">🥕 Ingrédients à éviter</div>
                {dislikes.filter(d => d.type === 'ingredient').map(d => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: 'white', borderRadius: 10, marginBottom: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <span style={{ fontSize: 18 }}>🚫</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{d.name}</span>
                    <button onClick={() => removeDislike(d.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#CCCCDD' }}>×</button>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* MODAL REPAS */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">
              {mealForm.meal_type === 'midi' ? '🌞 Repas du midi' : '🌙 Repas du soir'}
              <span style={{ fontSize: 12, fontWeight: 400, color: '#8888AA', marginLeft: 8 }}>
                {new Date(mealForm.meal_date + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
            </div>

            {/* IA suggestion */}
            <div style={{ background: '#EEF0FF', borderRadius: 12, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6C63FF', marginBottom: 8 }}>✨ Suggestion IA</div>
              <input className="form-input" placeholder="Ingrédients disponibles..." value={aiForm.ingredients} onChange={e => setAiForm({ ...aiForm, ingredients: e.target.value })} style={{ fontSize: 13, marginBottom: 8 }} />
              <input className="form-input" placeholder="Préférences / contraintes..." value={aiForm.preferences} onChange={e => setAiForm({ ...aiForm, preferences: e.target.value })} style={{ fontSize: 13, marginBottom: 8 }} />
              <button className="btn btn-primary btn-sm" onClick={getSuggestion} disabled={aiLoading} style={{ width: '100%' }}>
                {aiLoading ? <><span className="spinner" style={{ width: 12, height: 12, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Génération...</> : '✨ Suggérer'}
              </button>
              {aiSuggest && <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'white', borderRadius: 8, padding: 10 }}>{aiSuggest}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Nom du plat</label>
              <input className="form-input" placeholder="Ex: Poulet rôti..." value={mealForm.title} onChange={e => setMealForm({ ...mealForm, title: e.target.value })} />
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label className="form-label" style={{ margin: 0 }}>Ingrédients</label>
                <button type="button" onClick={() => setEditingIngredients(e => !e)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#8888AA', fontWeight: 600 }}>
                  {editingIngredients ? '✓ Voir' : '✏️ Modifier'}
                </button>
              </div>
              {editingIngredients ? (
                <textarea className="form-input" rows={Math.max(3, (mealForm.ingredients || '').split(',').length + 1)}
                  placeholder={"poulet\nthym\npommes de terre"}
                  value={(mealForm.ingredients || '').split(',').map(s => s.trim()).join('\n')}
                  onChange={e => setMealForm({ ...mealForm, ingredients: e.target.value.split('\n').map(s => s.trim()).filter(Boolean).join(', ') })}
                  style={{ resize: 'none', lineHeight: 1.8, fontFamily: 'inherit' }} autoFocus />
              ) : (
                <div style={{ border: '1.5px solid #E0E4F0', borderRadius: 10, background: '#F8F9FF', overflow: 'hidden' }}>
                  {(mealForm.ingredients || '').split(',').map(s => s.trim()).filter(Boolean).map((ing, i, arr) => (
                    <div key={i} onClick={() => navigate(`/courses?ingredient=${encodeURIComponent(ing)}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', borderBottom: i < arr.length - 1 ? '1px solid #EEEEFF' : 'none', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#EEEEFF'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: 13 }}>🛒</span>
                      <span style={{ fontSize: 14, color: '#3355AA', flex: 1 }}>{ing}</span>
                      <span style={{ fontSize: 11, color: '#AAAACC' }}>→</span>
                    </div>
                  ))}
                  {!mealForm.ingredients && (
                    <div style={{ padding: '10px 12px', color: '#CCCCDD', fontSize: 13 }}>Aucun ingrédient — cliquer sur ✏️ Modifier</div>
                  )}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Note</label>
              <input className="form-input" placeholder="Note..." value={mealForm.description} onChange={e => setMealForm({ ...mealForm, description: e.target.value })} />
            </div>

            <div className="form-group">
              <label className="form-label">Cuisson</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[['', '🚫 Aucune'], ['plaque', '🔥 Plaque'], ['four', '🫙 Four'], ['robot', '🤖 Silvercrest'], ['robot+four', '🤖🫙 Robot+Four']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setMealForm({ ...mealForm, appliance: val })}
                    style={{ padding: '6px 12px', borderRadius: 20, border: mealForm.appliance === val ? '2px solid #6C63FF' : '2px solid #E0E4F0', background: mealForm.appliance === val ? '#EEF0FF' : 'white', fontSize: 13, cursor: 'pointer', fontWeight: mealForm.appliance === val ? 700 : 400, color: mealForm.appliance === val ? '#6C63FF' : '#666688' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Lien recette (optionnel)</label>
              <input className="form-input" placeholder="https://..." value={mealForm.recipe_url} onChange={e => setMealForm({ ...mealForm, recipe_url: e.target.value })} />
            </div>

            {/* Recherche de recette */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#8888AA', marginBottom: 8 }}>🔍 Trouver une recette</div>
              {mealForm.recipe_url ? (
                <a href={mealForm.recipe_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', textAlign: 'center', background: '#6C63FF', color: 'white', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
                  📖 Voir la recette
                </a>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  {mealForm.title && <>
                    <a href={`https://www.marmiton.org/recettes/recherche.aspx?aqt=${encodeURIComponent(mealForm.title)}`} target="_blank" rel="noopener noreferrer"
                      style={{ flex: 1, textAlign: 'center', background: '#FF6B35', color: 'white', borderRadius: 10, padding: '8px 4px', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                      🍳 Marmiton
                    </a>
                    <a href={`https://jow.fr/search?q=${encodeURIComponent(mealForm.title)}`} target="_blank" rel="noopener noreferrer"
                      style={{ flex: 1, textAlign: 'center', background: '#00C9A7', color: 'white', borderRadius: 10, padding: '8px 4px', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                      🥗 JOW
                    </a>
                    <a href={`https://www.monsieur-cuisine.com/fr/recettes/?tx_sfrecipes_recipes[search]=${encodeURIComponent(mealForm.title)}`} target="_blank" rel="noopener noreferrer"
                      style={{ flex: 1, textAlign: 'center', background: '#E91E8C', color: 'white', borderRadius: 10, padding: '8px 4px', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                      🤖 M. Cuisine
                    </a>
                  </>}
                  {!mealForm.title && <div className="text-muted" style={{ fontSize: 12 }}>Saisissez un nom de plat pour rechercher une recette</div>}
                </div>
              )}
            </div>

            {editMeal && (editMeal.prep_time || editMeal.cook_time) && (
              <div style={{ display: 'flex', gap: 8, background: '#F0F4FF', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#8888AA', fontWeight: 700, textTransform: 'uppercase' }}>Préparation</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#6C63FF' }}>{editMeal.prep_time ?? '–'} min</div>
                </div>
                <div style={{ width: 1, background: '#E0E4F0' }} />
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#8888AA', fontWeight: 700, textTransform: 'uppercase' }}>Cuisson</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#6C63FF' }}>{editMeal.cook_time ?? '–'} min</div>
                </div>
                <div style={{ width: 1, background: '#E0E4F0' }} />
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#8888AA', fontWeight: 700, textTransform: 'uppercase' }}>Total</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#4CAF50' }}>{(editMeal.prep_time || 0) + (editMeal.cook_time || 0)} min</div>
                </div>
              </div>
            )}

            {editMeal && swapMode && (
              <div style={{ background: '#FFF8E1', borderRadius: 12, padding: 12, marginBottom: 12, border: '1.5px solid #FFD54F' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#E65100', marginBottom: 8 }}>↔️ Échanger avec quel repas ?</div>
                {meals.filter(m => m.id !== editMeal.id && !m.is_skip).map(m => {
                  const d = new Date(m.meal_date + 'T00:00');
                  const label = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
                  return (
                    <button key={m.id} onClick={() => swapWith(m)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', background: 'white', border: '1px solid #FFD54F', borderRadius: 8, padding: '7px 10px', marginBottom: 4, cursor: 'pointer', fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: '#E65100', marginRight: 6 }}>{label}</span>
                      {m.title}
                      {(m.prep_time || m.cook_time) && <span style={{ color: '#8888AA', fontSize: 11, marginLeft: 6 }}>⏱️ {(m.prep_time||0)+(m.cook_time||0)} min</span>}
                    </button>
                  );
                })}
                <button onClick={() => setSwapMode(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8888AA', fontSize: 12, marginTop: 4 }}>Annuler</button>
              </div>
            )}

            {editMeal && !swapMode && (
              <button onClick={() => setSwapMode(true)}
                style={{ width: '100%', marginBottom: 8, background: '#F5F5F8', border: '1.5px solid #E0E4F0', borderRadius: 10, color: '#6C63FF', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '9px 0' }}>
                ↔️ Échanger avec un autre jour
              </button>
            )}

            {editMeal && (
              <button onClick={async () => { await markSkip(editMeal.meal_date, editMeal.meal_type); setShowForm(false); }}
                style={{ width: '100%', marginBottom: 10, background: '#F5F5F8', border: '1.5px dashed #CCCCDD', borderRadius: 10, color: '#888AAA', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '10px 0' }}>
                🍴 On mange dehors ce jour-là
              </button>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              {editMeal && (
                <>
                  <button className="btn btn-danger btn-sm" onClick={deleteMeal} title="Supprimer">🗑️</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { replaceMeal(editMeal); setShowForm(false); }} title="Autre suggestion (ce plat reste disponible)">🔄</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { dislikeMeal(editMeal); setShowForm(false); }} title="Ne plus proposer ce plat">👎</button>
                </>
              )}
              <button className="btn btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={saveMeal} disabled={saving} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
